/**
 * The Diagnostics drawer — the client-side half of the failure story.
 *
 * Opened with Ctrl+Shift+D from anywhere in the shell, it lists the API calls this tab has
 * made (shared/diagnostics/request-log.ts): method, path, status, duration, and — for the
 * failures — the backend's own code, message, and `requestId`. "Errors only" is the default
 * view, because that is what someone opens this for.
 *
 * Why it exists next to the server-side Admin → Errors screen: this one shows what the SERVER
 * never saw. A request blocked by CORS, a session that expired mid-save, a timeout, an offline
 * moment — none of those produce a server row, and all of them produce a user complaint. It is
 * also available to any signed-in user, not just an admin, so support can say "press
 * Ctrl+Shift+D and send me the copy" without granting anyone a capability.
 *
 * The copy action puts the whole buffer on the clipboard as JSON. Bodies and headers were
 * never recorded (see request-log.ts), so the dump is safe to paste into a ticket.
 */

import { BugOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { App, Badge, Button, Descriptions, Drawer, Empty, Flex, Space, Switch, Table, Tag, Typography } from 'antd';
import { useMemo, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import {
  clearRequestLog,
  exportRequestLog,
  getRequestLog,
  subscribeToRequestLog,
} from './request-log';

import type { RequestLogEntry } from './request-log';
import type { DescriptionsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';

export interface DiagnosticsDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** Subscribe this component to the module-level ring buffer. */
function useRequestLog(): readonly RequestLogEntry[] {
  return useSyncExternalStore(subscribeToRequestLog, getRequestLog, getRequestLog);
}

/** Colour a status the way an operator reads it: red = broken, orange = rejected, green = fine. */
function statusColor(status: number | undefined): string {
  if (status === undefined) return 'red';
  if (status >= 500) return 'red';
  if (status >= 400) return 'orange';
  return 'green';
}

/** Time-of-day only: the buffer never spans a day, and a full ISO stamp crowds the row. */
function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString();
}

/** The expanded failure detail: only the fields this particular failure actually carried. */
function detailItems(
  entry: RequestLogEntry,
  t: (key: string) => string,
): DescriptionsProps['items'] {
  const items: NonNullable<DescriptionsProps['items']> = [];
  if (entry.code !== undefined) {
    items.push({
      key: 'code',
      label: t('diagnostics.detail.code'),
      children: <Typography.Text code>{entry.code}</Typography.Text>,
    });
  }
  if (entry.message !== undefined) {
    items.push({ key: 'message', label: t('diagnostics.detail.message'), children: entry.message });
  }
  if (entry.requestId !== undefined) {
    items.push({
      key: 'requestId',
      label: t('diagnostics.detail.requestId'),
      // The lookup key for Admin → Errors and the server log — copyable on its own.
      children: (
        <Typography.Text code copyable>
          {entry.requestId}
        </Typography.Text>
      ),
    });
  }
  if (entry.details !== undefined) {
    items.push({
      key: 'details',
      label: t('diagnostics.detail.details'),
      children: (
        <Typography.Text code style={{ whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(entry.details, null, 2)}
        </Typography.Text>
      ),
    });
  }
  return items;
}

export function DiagnosticsDrawer({ open, onClose }: DiagnosticsDrawerProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { message } = App.useApp();
  const [errorsOnly, setErrorsOnly] = useState(true);
  const entries = useRequestLog();

  // Newest-first: the failure being investigated is almost always the last thing that happened.
  const rows = useMemo(() => {
    const ordered = [...entries].reverse();
    return errorsOnly ? ordered.filter((entry) => entry.isError) : ordered;
  }, [entries, errorsOnly]);

  const errorCount = useMemo(() => entries.filter((entry) => entry.isError).length, [entries]);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(exportRequestLog());
      void message.success(t('diagnostics.copied'));
    } catch {
      // Clipboard access can be denied (insecure context, permission policy). Say so rather
      // than claiming a copy that did not happen.
      void message.error(t('diagnostics.copyFailed'));
    }
  }

  const columns: ColumnsType<RequestLogEntry> = [
    {
      title: t('diagnostics.column.time'),
      dataIndex: 'at',
      width: 96,
      render: (at: string) => <Typography.Text type="secondary">{clockTime(at)}</Typography.Text>,
    },
    {
      title: t('diagnostics.column.status'),
      dataIndex: 'status',
      width: 92,
      render: (status: number | undefined) => (
        <Tag color={statusColor(status)}>{status ?? t('diagnostics.noResponse')}</Tag>
      ),
    },
    {
      title: t('diagnostics.column.request'),
      dataIndex: 'url',
      render: (url: string, entry) => (
        <Typography.Text code copyable={{ text: url }}>
          {entry.method} {url}
        </Typography.Text>
      ),
    },
    {
      title: t('diagnostics.column.duration'),
      dataIndex: 'durationMs',
      width: 88,
      align: 'right',
      render: (durationMs: number) => `${durationMs} ms`,
    },
  ];

  return (
    <Drawer
      title={
        <Space>
          <BugOutlined />
          {t('diagnostics.title')}
          <Badge count={errorCount} />
        </Space>
      }
      width={840}
      open={open}
      onClose={onClose}
      extra={
        <Space>
          <Switch
            checked={errorsOnly}
            onChange={setErrorsOnly}
            checkedChildren={t('diagnostics.errorsOnly')}
            unCheckedChildren={t('diagnostics.allCalls')}
          />
          <Button icon={<CopyOutlined />} onClick={() => void handleCopy()}>
            {t('diagnostics.copy')}
          </Button>
          <Button icon={<DeleteOutlined />} onClick={clearRequestLog}>
            {t('diagnostics.clear')}
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">{t('diagnostics.hint')}</Typography.Paragraph>
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: <Empty description={t('diagnostics.empty')} /> }}
        // Only a failed call has anything more to show; a 200 row would expand to nothing.
        expandable={{
          rowExpandable: (entry) => entry.isError,
          expandedRowRender: (entry) => (
            // `items` rather than <Descriptions.Item> children: half of these rows are
            // conditional, and a `false` child is not something Descriptions accepts.
            <Descriptions size="small" column={1} bordered items={detailItems(entry, t)} />
          ),
        }}
      />
      <Flex justify="flex-end" style={{ marginTop: 12 }}>
        <Typography.Text type="secondary">
          {t('diagnostics.captured', { count: entries.length })}
        </Typography.Text>
      </Flex>
    </Drawer>
  );
}
